import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
} from "react-router-dom";
import { Toaster } from "sonner";

import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { AppLayout } from "@/components/layout/AppLayout";
import { QueryProvider } from "@/providers/QueryProvider";
import { RealtimeProvider } from "@/providers/RealtimeProvider";
import { DashboardPage } from "@/pages/DashboardPage";
import { useLocation } from "react-router-dom";

import { AcceptInvitePage } from "@/pages/AcceptInvitePage";
import { useMe } from "@/hooks/api/useAuth";
import { isLandlord } from "@/lib/roles";
import { LoginPage } from "@/pages/LoginPage";
import { SignPage } from "@/pages/SignPage";
import { SignupPage } from "@/pages/SignupPage";
import { VerifyEmailPage } from "@/pages/VerifyEmailPage";
import { PlaceholderPage } from "@/pages/PlaceholderPage";
import { ContractDetailPage } from "@/pages/ContractDetailPage";
import { ContractsPage } from "@/pages/ContractsPage";
import { CrmPage } from "@/pages/CrmPage";
import { DocumentStudioPage } from "@/pages/DocumentStudioPage";
import { NotificationsPage } from "@/pages/NotificationsPage";
import { ApplicationDetailPage } from "@/pages/ApplicationDetailPage";
import { ApplicationsPage } from "@/pages/ApplicationsPage";
import { InboxPage } from "@/pages/InboxPage";
import { InboxMessagePage } from "@/pages/InboxMessagePage";
import { InboxSettingsPage } from "@/pages/InboxSettingsPage";
import { LandlordDetailPage } from "@/pages/LandlordDetailPage";
import { LetterheadPage } from "@/pages/LetterheadPage";
import { TeamPage } from "@/pages/TeamPage";
import { TenantDetailPage } from "@/pages/TenantDetailPage";
import { GuarantorDetailPage } from "@/pages/GuarantorDetailPage";
import { TenantsPage } from "@/pages/TenantsPage";
import { InspectionDetailPage } from "@/pages/InspectionDetailPage";
import { InspectionsPage } from "@/pages/InspectionsPage";
import { InvoiceDetailPage } from "@/pages/InvoiceDetailPage";
import { InvoicesPage } from "@/pages/InvoicesPage";
import { LedgerPage } from "@/pages/LedgerPage";
import { MailingPage } from "@/pages/MailingPage";
import { PartiesPage } from "@/pages/PartiesPage";
import { PropertiesPage } from "@/pages/PropertiesPage";
import { PropertyDetailPage } from "@/pages/PropertyDetailPage";
import { SettingsPage } from "@/pages/SettingsPage";
import { SubscribersPage } from "@/pages/SubscribersPage";
import { ViewingDetailPage } from "@/pages/ViewingDetailPage";
import { ViewingsPage } from "@/pages/ViewingsPage";

const SECTIONS: { path: string; title: string }[] = [];

// Landlord-portal guests only reach their four surfaces; everything else bounces
// home (the API already 403s — this keeps the UI from rendering broken pages).
const LANDLORD_PREFIXES = ["/dashboard", "/properties", "/applications", "/contracts"];

function LandlordScope({ children }: { children: React.ReactNode }) {
  const { data: me } = useMe();
  const location = useLocation();
  if (
    isLandlord(me) &&
    !LANDLORD_PREFIXES.some((p) => location.pathname.startsWith(p))
  ) {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/sign/:token" element={<SignPage />} />
      <Route path="/accept-invite/:token" element={<AcceptInvitePage />} />
      {/* Full-display editor workspace — auth-guarded but outside the app sidebar. */}
      <Route
        path="/document-templates"
        element={
          <ProtectedRoute>
            <DocumentStudioPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppLayout>
              <LandlordScope>
                <Outlet />
              </LandlordScope>
            </AppLayout>
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="properties" element={<PropertiesPage />} />
        <Route path="properties/:id" element={<PropertyDetailPage />} />
        <Route path="parties" element={<PartiesPage />} />
        <Route path="parties/landlords/:id" element={<LandlordDetailPage />} />
        <Route path="parties/tenants/:id" element={<TenantDetailPage />} />
        <Route path="parties/guarantors/:id" element={<GuarantorDetailPage />} />
        <Route path="crm" element={<CrmPage />} />
        <Route path="contracts" element={<ContractsPage />} />
        <Route path="contracts/:id" element={<ContractDetailPage />} />
        <Route path="invoices" element={<InvoicesPage />} />
        <Route path="invoices/:id" element={<InvoiceDetailPage />} />
        <Route path="ledger" element={<LedgerPage />} />
        <Route path="viewings" element={<ViewingsPage />} />
        <Route path="viewings/:id" element={<ViewingDetailPage />} />
        <Route path="applications" element={<ApplicationsPage />} />
        <Route path="applications/:id" element={<ApplicationDetailPage />} />
        {/* Legacy bookmark: enquiries collapsed into applications (B3). */}
        <Route path="enquiries" element={<Navigate to="/applications" replace />} />
        <Route path="inbox" element={<InboxPage />} />
        <Route path="inbox/settings" element={<InboxSettingsPage />} />
        <Route path="inbox/:id" element={<InboxMessagePage />} />
        <Route path="subscribers" element={<SubscribersPage />} />
        <Route path="mailings" element={<MailingPage />} />
        <Route path="inspections" element={<InspectionsPage />} />
        <Route path="inspections/:id" element={<InspectionDetailPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="letterhead" element={<LetterheadPage />} />
        <Route path="agencies" element={<TenantsPage />} />
        <Route path="team" element={<TeamPage />} />
        <Route path="settings" element={<SettingsPage />} />
        {SECTIONS.map(({ path, title }) => (
          <Route key={path} path={path.slice(1)} element={<PlaceholderPage title={title} />} />
        ))}
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <QueryProvider>
      <RealtimeProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </RealtimeProvider>
    </QueryProvider>
  );
}
